/**
 * CommitmentLedger — the Commitment list in the canonical QUEUE GRAMMAR.
 *
 * The commitment analog of `GoalsLedger` / the live tasks queue: ONE Card whose
 * flush caption is a segmented state-filter bar (dot · label · count pills),
 * capping a FLAT, dense, single-line zebra row list. A row here is visually
 * identical to a Work queue row (`GroupedTaskList`) / a goal ledger row, so a
 * commitment looks the same whether you meet it in Work (owner lens) or on a
 * Pulse Lane (lifecycle-stage lens) — one grammar, two lenses.
 *
 * This is the LIST form. The column/sort form (`CommitmentTable`, a `DataTable`)
 * stays for genuinely tabular, sort-by-column contexts (the mode Objects index).
 * The segment filter mirrors the badge intents (`commitmentStateIntent`) so the
 * legend can never disagree with a row's pill.
 *
 * Every row links to its canonical object page (`objectHref(uid, from)`); the
 * `focus-<uid>` id preserves the Play "dive-in" scroll anchor (§2.6).
 */

import * as React from "react";
import { ObjectLink } from "./object-link";
import { ClipboardList, FileText, Boxes } from "../icons";
import { Card, CardContent } from "../card";
import { EmptyState, type EmptyStateProps } from "./empty-state";
import { IntentBadge } from "./status-badge";
import { commitmentStateIntent, type CommitmentTableRow } from "./commitment-table";
import { objectHref } from "../path-label";
import { INTENT_CLASSES, type Intent } from "../tokens";
import { formatRelativeTime } from "../format";
import { cn } from "../utils";

const KIND_GLYPH: Record<string, React.ComponentType<{ className?: string }>> = {
  task: ClipboardList,
  prd: FileText,
  release: Boxes,
};

const stateLabel = (s: string) =>
  s.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

// Segments mirror the badge intents (one source of truth → legend can't drift).
type Seg = "all" | Intent;
const SEGMENTS: { key: Seg; label: string; intent: Intent }[] = [
  { key: "all", label: "All", intent: "neutral" },
  { key: "info", label: "Open", intent: "info" },
  { key: "warning", label: "At-risk", intent: "warning" },
  { key: "success", label: "Fulfilled", intent: "success" },
  { key: "danger", label: "Cancelled", intent: "danger" },
];

export interface CommitmentLedgerProps {
  rows: CommitmentTableRow[];
  /** Mode stamped on each `/o/<uid>` link (`?from=`) so the chrome stays anchored. */
  from?: string;
  /** Cap the VISIBLE rows (segment counts still reflect the full set). */
  limit?: number;
  empty?: React.ReactNode;
  /**
   * Rich blank state (icon + heading + guidance + a "do this next" action)
   * shown when the ledger is empty. Takes precedence over `empty` — reach for
   * it instead of a bare string so an empty stage reads as an intentional panel
   * with a next step (e.g. the Lane cockpit's Objects region).
   */
  emptyState?: Omit<EmptyStateProps, "variant" | "colSpan">;
  className?: string;
}

export function CommitmentLedger({
  rows,
  from,
  limit,
  empty = "No commitments in this view.",
  emptyState,
  className,
}: CommitmentLedgerProps) {
  const [seg, setSeg] = React.useState<Seg>("all");

  // A segment that no row matches is dropped (so we never show an empty "0" tab
  // for a state this lane never produces); "All" always shows.
  const visibleSegs = SEGMENTS.filter(
    (s) => s.key === "all" || rows.some((r) => commitmentStateIntent(r.state) === s.intent),
  );
  const count = (s: Seg) =>
    s === "all" ? rows.length : rows.filter((r) => commitmentStateIntent(r.state) === s).length;

  const filtered = seg === "all" ? rows : rows.filter((r) => commitmentStateIntent(r.state) === seg);
  const shown = limit != null ? filtered.slice(0, limit) : filtered;
  const hidden = filtered.length - shown.length;

  return (
    <Card className={cn("gap-0 overflow-hidden py-0", className)}>
      <CardContent className="px-0">
        {/* Segmented state filter — the queue's status-filter bar, reused. */}
        <header className="flex items-center justify-between gap-3 border-b border-border px-3 py-2">
          <div
            role="tablist"
            aria-label="Filter by state"
            className="-mx-1 flex items-center gap-1 overflow-x-auto px-1"
          >
            {visibleSegs.map(({ key, label, intent }) => {
              const on = seg === key;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => setSeg(key)}
                  className={cn(
                    "inline-flex h-7 shrink-0 items-center gap-2 rounded-md px-2.5 type-body transition-colors",
                    on
                      ? "bg-surface-3 text-foreground"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground",
                  )}
                >
                  <span aria-hidden className={cn("size-2 rounded-full", INTENT_CLASSES[intent].dot)} />
                  {label}
                  <span className="type-subtext tabular-nums text-muted-foreground">{count(key)}</span>
                </button>
              );
            })}
          </div>
        </header>

        {shown.length === 0 ? (
          emptyState ? (
            <EmptyState
              variant="inline"
              {...emptyState}
              className={cn(
                "flex min-h-52 items-center justify-center",
                emptyState.className,
              )}
            />
          ) : (
            <div className="px-6 py-12 text-center type-body text-muted-foreground">{empty}</div>
          )
        ) : (
          <div className="flex flex-col">
            {shown.map((r, i) => {
              const Glyph = KIND_GLYPH[r.kind] ?? ClipboardList;
              return (
                <ObjectLink
                  key={r.uid}
                  id={`focus-${r.uid}`}
                  uid={r.uid}
                  from={from}
                  className={cn(
                    "group flex w-full items-center gap-2.5 border-b border-border px-6 py-3 text-left type-body transition-colors last:border-b-0 hover:bg-secondary",
                    i % 2 === 1 && "bg-foreground/[0.025]",
                  )}
                >
                  <Glyph className="size-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 max-w-[48%] shrink-0 truncate text-foreground">{r.title}</span>
                  <IntentBadge intent={commitmentStateIntent(r.state)} withDot>
                    {stateLabel(r.state)}
                  </IntentBadge>
                  <span className="min-w-0 flex-1 truncate type-subtext text-muted-foreground">
                    {r.ownerLabel ?? "Unassigned"}
                    {r.targetOutcome ? ` · ${r.targetOutcome}` : ""}
                  </span>
                  <span className="w-14 shrink-0 text-right type-subtext tabular-nums text-muted-foreground">
                    {formatRelativeTime(r.updatedAt)}
                  </span>
                </ObjectLink>
              );
            })}
            {hidden > 0 ? (
              <div className="px-6 py-2 type-subtext text-muted-foreground">+{hidden} more</div>
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
